<template>
  <div class="flex-col w-full h-full bg-white rounded-xl p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex flex-wrap gap-x-4 gap-y-2">
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.batch_id") + ":"
          }}</span>
          <span class="text-primary">{{ batchDetail?.batch_id }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.organization") + ":"
          }}</span>
          <span>{{ batchDetail?.organisation }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.package") + ":"
          }}</span>
          <span>{{ translatedPackages }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.entity") + ":"
          }}</span>
          <span>{{ translatedEntityType }}</span>
        </div>
      </div>
      <div class="flex items-center">
        <div class="flex flex-wrap gap-4">
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.add_row`)"
              type="primary"
              outline
              @click="addRow"
            />
          </div>
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.copy_grid`)"
              type="primary"
              outline
              @click="copyGrid"
            />
          </div>
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.clear_grid`)"
              @click="clearGrid"
              type="primary"
              outline
            />
          </div>
          <div class="flex items-center gap-2">
            <Button
              :text="$t(`pages.batch_processing_table.download_xlsx`)"
              type="primary"
              outline
              @click="downloadXLSX"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="batchDetailLoading"
      class="flex items-center justify-center h-96"
    >
      <!-- <span class="text-gray-500">{{ $t("general.loading") }}</span> -->
      <Loader />
    </div>
    <div v-else style="max-width: 100%; overflow-x: auto">
      <div
        ref="tabulatorRef"
        style="margin-bottom: 18px; margin-top: 18px; min-width: 600px"
      ></div>
    </div>

    <div class="flex items-center justify-between mt-4">
      <div class="flex flex-wrap gap-2">
        <span class="italic">
          {{ $t(`pages.batch_processing_table.tip_use`) }}
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.ctrl_c_ctrl_c`)
          }}</span>
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.to_copy_paste`)
          }}</span>
          {{ $t(`pages.batch_processing_table.rows_with`) }}
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.excel_sheet_this`)
          }}</span>
          {{ $t(`pages.batch_processing_table.grid`) }}
        </span>
      </div>
      <div class="flex items-center gap-4" v-show="isEditPermission">
        <div class="flex items-center gap-2">
          <Button
            @click="handleSave"
            :text="$t(`actions.save`)"
            type="primary"
            :disabled="!anyRowExist"
            :loader="saveLoader"
          />
        </div>
        <div class="flex items-center gap-2">
          <Button
            @click="submitTheData"
            :text="$t(`actions.submit`)"
            type="success"
            :disabled="!anyRowExist"
            :disable="anyRowExist"
            :loader="submitLoadig"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-irregular-whitespace */
import * as XLSX from "xlsx";
import Button from "@/components/button";
import BatchAxios from "../batch-axios";
import { checkPermission } from "@shared/utils/functions";
import { EventBus } from "@/main.js";
// import { sampleData } from "./newData1";
import Loader from "@shared/loader";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation"
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";


window.XLSX = XLSX;

export default {
  name: "TabulatorGrid",
  data() {
    return {
      lang: "en",
      table: null,
      batchDetailLoading: false,
      editingRowIndex: null,
      rowData: [],
      columnMeta: [],
      saveLoader: false,
      submitLoadig: false,
      editableIfDependencyMap: {},
      columns: [],
      addedFields: new Set(),
      columnGenerating: false,
      maxRowLimit: 10000, // Set a maximum row limit
    };
  },
  props: {
    batchDetail: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    translatedPackages() {
        return translateEntity(this.batchDetail, caseEntityTypesMap.PACKAGE)?.package_name;
    },
    translatedEntityType() {
        return translateEntity(this.batchDetail, caseEntityTypesMap.ENTITY_TYPE)?.entity_type;
    },
    getAction() {
      return this.$route.params.action;
    },
    getBatchId() {
      return this.$route.params.id;
    },
    anyRowExist() {
      return this.table?.getDataCount() > 0 ?? false;
    },
    isEditPermission() {
      return (
        this.$route.params.action === "edit" &&
        (checkPermission("batchProcessing.edit") || checkPermission("batchProcessing.create"))
      );
    },
    isMaxRowLimitReached() {
      return this.table?.getDataCount() >= this.maxRowLimit;
    },
  },
  components: {
    Button,
    Loader,
  },
  async mounted() {
    await this.fetchBatchTabularData();
    this.$nextTick(() => {
      this.initGrid();
    });
    EventBus.$on("changeLanguageProfile", () => {
      this.refreshTheGrid();
    })
  },
  beforeDestroy() {
    EventBus.$off("changeLanguageProfile");
  },
  methods: {
    checkPermission,
    translateEntity,
    caseEntityTypesMap,
    async fetchBatchTabularData() {
      try {
        this.batchDetailLoading = true;
        const { data } = await BatchAxios.get(
          `/batch-processing/get-batch-form-fields/${this.getBatchId}?use_groupkey=true`
        );
        this.columnMeta = data.headers.filter(col => 
          !(col.field === 'tpi/asp_or_non_tpi' || 
            col.field === 'can_you_provide_proof_of_establishment?' || 
            col.parentField === 'tpi/asp_or_non_tpi')
        );
        this.rowData = data.data;
        // this.columnMeta = sampleData.headers;
        // this.rowData = sampleData.data;
        this.batchDetailLoading = false;
      } catch (error) {
        console.error("err", error);
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
        this.batchDetailLoading = false;
      }
    },
    handleReset(cell) {
      const field = cell.getField();
      const row = cell.getRow();

      // Reset groupKey fields when their parent changes
      if (field === "panasonic_region") {
        row.update({ operating_company: "", panasonic_entity: "" });
      } else if (field === "operating_company") {
        row.update({ panasonic_entity: "" });
      }

      // else if (field === 'entity_name') {
      //   row.update({ entity_type: '' });
      // }

      // Find dependent fields in columnMeta and reset them
      this.columnMeta.forEach((col) => {
        if (col.editableIf && col.editableIf.field === field) {
          if (col.groupKey) {
            row.update({ [col.groupKey]: "" });
          } else {
            row.update({ [col.field]: "" });
            const childCell = row.getCell(col.field);
            if (childCell) childCell.clearEdited();
          }
        }
      });
    },

    generateColumns() {
      try {
        this.columnGenerating = true;
        // Reset before each generation
        this.columns = [];
        this.addedFields = new Set();

        // 1. Index fields for quick lookup
        const fieldByName = {};
        (this.columnMeta || []).forEach((col) => {
          fieldByName[col.field] = col;
        });

        // 2. Find all root-level (no parentField, not grand_child)
        const roots = (this.columnMeta || []).filter(
          (f) => !f.parentField && !f.grand_child
        );

        // 4. Walk from all roots
        roots.forEach((root) => this.buildCol(root, this));

        // 5. Add stand-alone columns not in hierarchy (skip grand_child columns as they're handled in buildCol)
        (this.columnMeta || []).forEach((col) => {
          if (!col.grand_child && !this.addedFields.has(col.field)) {
            this.buildCol(col, this);
          }
        });

        // 6. Add Actions column (at end, for edit mode)
        if (this.isEditPermission) {
          this.columns.push({
            title: this.$t("general.actions"),
            field: "actions",
            formatter: () => "🗑️",
            width: 70,
            minWidth: 70,
            headerTooltip: "Delete row",
            headerSort: false,
            cellClick: (e, cell) => cell.getRow().delete(),
          });
        }
        return this.columns;
      } catch (err) {
        console.error("error in generateColumns", err);
        return [];
      } finally {
        this.columnGenerating = false;
      }
    },

    // 3. Recursive function to walk parent-child-grand_child
    buildCol(col, context) {
      if (!col) return;

      // Create unique key for this column
      let uniqueKey;
      if (col.groupKey) {
        // For groupKey columns, create unique key based on parent and groupKey
        uniqueKey = col.parentField
          ? `${col.parentField}_${col.groupKey}`
          : col.groupKey;
      } else {
        // For regular columns, use field name
        uniqueKey = col.field;
      }

      // Skip if already added
      if (this.addedFields.has(uniqueKey)) return;
      this.addedFields.add(uniqueKey);

      let baseTitle = col.titles || col.field || "";
      if (baseTitle.length > 20) {
        baseTitle = baseTitle.substring(0, 50) + "...";
      }
      const title = col.required
        ? `${baseTitle}<span class="tabulator-custom-required-asterisk px-1"> * </span>`
        : baseTitle;

      // Use groupKey as field name for groupKey columns
      const fieldToUse = col.groupKey || col.field;

      // // Set column width based on field type
      // const getColumnWidth = (col) => {
      //   if (col.groupKey === 'panasonic_region') return 200;
      //   if (col.groupKey === 'operating_company') return 250;
      //   if (col.groupKey === 'panasonic_entity') return 300;
      //   return 180;
      // };

      const def = {
        ...col,
        field: fieldToUse,
        title,
        // width: 300,
        minWidth: 150,
        headerTooltip: col.titles || col.field,
        headerSort: true,
        editor: col.editor || "input",
        editorParams: col.groupKey
          ? (cell) => {
              const row = cell.getRow().getData();

              if (col.groupKey === "panasonic_entity") {
                const operatingCompany = row["operating_company"];
                const region = row["panasonic_region"];
                // Debug: Check all operating_company fields and their conditions
                const allOperatingCompanyFields = context.columnMeta.filter(
                  (c) => c.groupKey === "operating_company"
                );

                // Check which operating_company field should be active for current region
                const correctOperatingCompanyField =
                  allOperatingCompanyFields.find(
                    (c) => c.editableIf && c.editableIf.value === region
                  );
               
                if (operatingCompany && region) {

                  const allPansonicEntityFields = context.columnMeta.filter(
                    (c) => c.groupKey === "panasonic_entity"
                  );

                  const correctPanasonnicEntityField =
                    allPansonicEntityFields.find(
                      (c) =>
                        c.parentField === correctOperatingCompanyField.field &&
                        c.parentValue === operatingCompany
                    );

                  if (
                    correctPanasonnicEntityField &&
                    correctPanasonnicEntityField.editorParams
                  ) {
                    return correctPanasonnicEntityField.editorParams;
                  } else {
                    return { values: [] };
                  }

                  // Fallback: if no specific field found, try to find any panasonic_entity field for this region
                  // const fallbackField = context.columnMeta.find(c =>
                  //   c.groupKey === 'panasonic_entity' &&
                  //   c.grand_child &&
                  //   c.field.startsWith(regionPrefix + '_')
                  // );
                  // if (fallbackField && fallbackField.editorParams) {
                  //   return fallbackField.editorParams;
                  // }
                }
              } else if (col.groupKey && row["panasonic_region"]) {
                // Generic logic for other groupKey fields that might need region-based selection
                const region = row["panasonic_region"];
                const regionMapping = {
                  Japan: "japan",
                  "Latin America": "latam",
                  "China & Northeast Asia": "china",
                  "Southeast Asia & Oceania": "seao",
                  "Europe & CIS": "europe",
                  "North America": "north",
                  "India, South Asia, Middle East & Africa": "isamea",
                };
                const regionPrefix = regionMapping[region];
                const activeField = context.columnMeta.find(
                  (c) =>
                    c.groupKey === col.groupKey &&
                    c.editableIf &&
                    row[c.editableIf.field] === c.editableIf.value &&
                    c.field.startsWith(regionPrefix + "_")
                );

                if (activeField && activeField.editorParams) {
                  return activeField.editorParams;
                }
              } else {
                const activeField = context.columnMeta.find(
                  (c) =>
                    c.groupKey === col.groupKey &&
                    c.editableIf &&
                    row[c.editableIf.field] === c.editableIf.value
                );

                if (activeField && activeField.editorParams) {
                  return activeField.editorParams;
                }
              }

              const params = col.editorParams || { values: [] };
              // Ensure proper structure for list editor
              if (!params.values || !Array.isArray(params.values)) {
                return { values: [] };
              }
              return params;
            }
          : col.editorParams || {},
        formatter:
          col.editor === "list"
            ? (cell) => {
                const value = cell.getValue();
                if (!value) return value;

                // Handle multiselect arrays
                if (Array.isArray(value)) {
                  let editorParams = col.editorParams;
                  if (typeof editorParams === 'function') {
                    editorParams = editorParams(cell);
                  }
                  if (editorParams?.values) {
                    const labels = value.map(val => {
                      const option = editorParams.values.find(opt => opt.value === val);
                      return option ? option.label : val;
                    });
                    return labels.join(', ');
                  }
                  return value.join(', ');
                }

                const fieldName = cell.getField();
                const row = cell.getRow().getData();

                if (fieldName === "operating_company") {
                  const region = row["panasonic_region"];
                  if (region) {
                    const activeField = context.columnMeta.find(
                      (c) => c.groupKey === "operating_company" && c.editableIf && c.editableIf.value === region
                    );
                    if (activeField?.editorParams?.values) {
                      const option = activeField.editorParams.values.find(opt => opt.value === value);
                      return option ? option.label : value;
                    }
                  }
                } else if (fieldName === "panasonic_entity") {
                  const operatingCompany = row["operating_company"];
                  const region = row["panasonic_region"];
                  if (operatingCompany && region) {
                    const allOperatingCompanyFields = context.columnMeta.filter(
                      (c) => c.groupKey === "operating_company"
                    );
                    const correctOperatingCompanyField = allOperatingCompanyFields.find(
                      (c) => c.editableIf && c.editableIf.value === region
                    );
                    const allPansonicEntityFields = context.columnMeta.filter(
                      (c) => c.groupKey === "panasonic_entity"
                    );
                    const correctPanasonicEntityField = allPansonicEntityFields.find(
                      (c) => c.parentField === correctOperatingCompanyField?.field && c.parentValue === operatingCompany
                    );
                    if (correctPanasonicEntityField?.editorParams?.values) {
                      const option = correctPanasonicEntityField.editorParams.values.find(opt => opt.value === value);
                      return option ? option.label : value;
                    }
                  }
                } else {
                  // Handle other list fields normally
                  let editorParams = col.editorParams;
                  if (typeof editorParams === 'function') {
                    editorParams = editorParams(cell);
                  }
                  if (editorParams?.values) {
                    const option = editorParams.values.find(opt => opt.value === value);
                    return option ? option.label : value;
                  }
                }
                
                return value;
              }
            : undefined,
        editable: context.isEditPermission
          ? (cell) => {
              const row = cell.getRow().getData();
              if (col.editableIf) {
                if (col.groupKey === "panasonic_entity") {
                  const operatingCompany = row["operating_company"];
                  const region = row["panasonic_region"];
                  if (!region || !operatingCompany) return false;

                    // Debug: Check all operating_company fields and their conditions
                    const allOperatingCompanyFields = context.columnMeta.filter(
                      (c) => c.groupKey === "operating_company"
                    );

                    // Check which operating_company field should be active for current region
                    const correctOperatingCompanyField =
                      allOperatingCompanyFields.find(
                        (c) => c.editableIf && c.editableIf.value === region
                      );


                  // Check if activeField exists for this combination
                  // const regionMapping = {
                  //   Japan: "japan",
                  //   "Latin America": "latam",
                  //   "China & Northeast Asia": "china",
                  //   "Southeast Asia & Oceania": "seao",
                  //   "Europe & CIS": "europe",
                  //   "North America": "north",
                  //   "India, South Asia, Middle East & Africa": "isamea",
                  // };
                  // const regionPrefix = regionMapping[region];

                  const allPansonicEntityFields = context.columnMeta.filter(
                    (c) => c.groupKey === "panasonic_entity" && c.grand_child
                  );

                   const correctPanasonnicEntityField =
                    allPansonicEntityFields.find(
                      (c) =>
                        // c.editableIf && c.editableIf.field === correctPanasonnicEntityField.field &&
                        c.parentField === correctOperatingCompanyField?.field &&
                        c.parentValue === operatingCompany
                    );
                  
                  return !!correctPanasonnicEntityField;
                } else {
                  // Original logic for other fields
                  const fieldsWithSameGroupKey = context.columnMeta.filter(
                    (c) => c.groupKey === col.groupKey && c.editableIf
                  );
                  return fieldsWithSameGroupKey.some(
                    (field) =>
                      row[field.editableIf.field] === field.editableIf.value
                  );
                }
              }
              return true;
            }
          : false,
      };
      this.columns.push(def);

      // Find children
      const children = (context.columnMeta || []).filter(
        (f) => f.parentField === col.field && !f.grand_child
      );
      children.forEach((childCol) => this.buildCol(childCol, context));

      // Find grandchildren
      const grandChildren = (context.columnMeta || []).filter(
        (f) => f.parentField === col.field && f.grand_child
      );
      grandChildren.forEach((grandCol) => this.buildCol(grandCol, context));
    },
    initGrid() {
      const columns = this.generateColumns() || [];
      const data = this.table
        ? this.table.getData()
        : JSON.parse(JSON.stringify(this.rowData));
      const gridElement = this.$refs.tabulatorRef;
      if (!gridElement) {
        console.error("Tabulator init error: $refs.grid is undefined");
        return;
      }
      this.table = new window.Tabulator(gridElement, {
        height: "420px",
        layout: "fitDataTable",
        responsiveLayout: "fitData",
        // responsiveLayout: "scroll",
        clipboard: this.isEditPermission,
        rowHeader: {
          formatter: "rownum",
          headerSort: false,
          hozAlign: "center",
          resizable: false,
          frozen: true,
        },
        columns,
        data,
        ...(this.isEditPermission && {
          clipboardPasteParser: (clipboard) => {
            const rows = clipboard
              .trim()
              .split("\n")
              .map((row) => row.split("\t"));
            const allColumns = this.table.getColumns();
            const fields = allColumns.map((col) => col.getField());

            const dataFields = fields.filter(
              (f) => f && f !== "actions" && f !== "id"
            );

            // Check if first row contains headers by comparing with column titles
            const columnTitles = allColumns
              .map((col) => {
                const title = col.getDefinition().title;
                return typeof title === "string"
                  ? title
                      .replace(/<[^>]*>/g, "")
                      .replace(/\*/g, "")
                      .trim()
                      .toLowerCase()
                  : "";
              })
              .filter((t) => t);

            let dataRows = rows;
            if (rows.length > 0) {
              const firstRowLower = rows[0].map(
                (cell) => cell?.trim().toLowerCase() || ""
              );
              const hasHeaders = firstRowLower.some((cell) =>
                columnTitles.includes(cell)
              );
              if (hasHeaders) {
                dataRows = rows.slice(1);
              }
            }

            const existingIds = this.rowData.map((r) => r.id);
            let nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;

            const newRows = dataRows
              .filter((row) => row.some(cell => cell && cell.trim() !== "")) // Filter empty rows first
              .map((row) => {
                const obj = { id: nextId++ };
                dataFields.forEach((field, i) => {
                  let value = row[i]?.trim() || "";
                  
                  // Check if value looks like a JSON array string
                  if (value.startsWith('[') && value.endsWith(']')) {
                    try {
                      value = JSON.parse(value);
                      // console.log(`Parsed JSON array for ${field}:`, value);
                    } catch (e) {
                      console.log(`Failed to parse JSON for ${field}, keeping as string`);
                    }
                  }
                  
                  obj[field] = value;
                });
                return obj;
              })
              .filter((row) => {
                const values = Object.values(row).filter(val => val !== row.id);
                return values.some(val => val && val.toString().trim() !== "");
              });

            // Check if pasting would exceed row limit
            const currentRowCount = this.table.getDataCount();
            if (currentRowCount + newRows.length > this.maxRowLimit) {
              this.$toast.error(
                this.$t("pages.batch_processing_table.max_limit_reached")
              );
              return [];
            }

            this.rowData.push(...newRows);
            return newRows;
          },
        }),
      });

      // Add CSS class for view mode
      if (!this.isEditPermission) {
        gridElement.classList.add("view-mode");
      }

      this.table.on("cellEdited", (cell) => {
        // Fix object values issue but preserve arrays (multiselect)
        const value = cell.getValue();
        const field = cell.getField();
        // Check if this is a multiselect field
        const isMultiselect = this.columnMeta.some(meta => 
          ((meta.groupKey && meta.groupKey === field) || meta.field === field) &&
          meta.editor === "list" && 
          meta.editorParams?.multiselect
        );
        
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // If value is an object like {"": "Yes"}, extract the actual value
          const keys = Object.keys(value);
          if (keys.length === 1) {
            const actualValue = value[keys[0]];
            if (actualValue && actualValue !== '') {
              cell.setValue(actualValue);
            } else {
              cell.setValue('');
            }
          } else {
            cell.setValue('');
          }
        } else if (isMultiselect && typeof value === 'string' && value.includes(',')) {
          // For multiselect fields, convert comma-separated strings to arrays
          const arrayValue = value.split(',').map(v => v.trim()).filter(v => v !== '');
          cell.setValue(arrayValue);
        }
        // Arrays (multiselect values) are preserved as-is

        this.handleReset(cell);

        // Refresh dependent fields when parent changes
        const dependentFields = this.columnMeta.filter(
          (col) => col.editableIf && col.editableIf.field === field
        );

        if (dependentFields.length > 0) {
          // Redraw the table to refresh editor options
          this.table.redraw(true);
        }
      });
      // this.table.on("clipboardPaste", function(data) {
      //   // Remove any rows that are completely empty
      //   const cleanedData = data.filter(row =>
      //     Object.values(row).some(val => val && val.toString().trim() !== "")
      //   );
      //   this.table.updateData(cleanedData);
      // });
    },
    refreshGrid() {
      if (this.table) {
        const columns = this.generateColumns();
        this.table.setColumns(columns);
      }
    },
    addRow() {
      if (this.table?.getDataCount() >= this.maxRowLimit) {
        this.$toast.error(
          this.$t("pages.batch_processing_table.max_limit_reached")
        );
        return;
      }

      const ids = this.rowData.map((r) => r.id);
      const newId = ids.length ? Math.max(...ids) + 1 : 1;
      const emptyRow = { id: newId };

      this.columnMeta.forEach((col) => {
        if (!(col.field in emptyRow)) {
          emptyRow[col.field] = col.type === "boolean" ? false : "";
        }
      });
      this.table.addRow(emptyRow);
      this.rowData.push(emptyRow);
    },
    copyGrid() {
      this.table.copyToClipboard();
    },
    clearGrid() {
      this.table.clearData();
    },
    downloadXLSX() {
      const originalData = this.table.getData();
      console.log("originalData", originalData);
      const originalColumns = this.table.getColumnDefinitions();

      const cleanedData = originalData
        .filter((row) => {
          return Object.values(row).some((val) => {
            if (val === null || val === undefined || val === "") return false;
            if (Array.isArray(val) && val.length === 0) return false;
            if (typeof val === "string" && val.trim() === "") return false;
            return true;
          });
        })
        .map((row, rowIndex) => {
          const cleaned = {};
          const tableRow = this.table.getRows()[rowIndex];
          
          for (const key in row) {
            if (key === 'actions') continue; // Skip actions column
            
            let val = row[key];
            if (
              val === null ||
              val === undefined ||
              (Array.isArray(val) && val.length === 0)
            ) {
              cleaned[key] = "";
            } else {
              // Get displayed value from table cell
              if (tableRow) {
                try {
                  const cell = tableRow.getCell(key);
                  const element = cell.getElement();
                  const displayValue = element ? (element.textContent || element.innerText) : '';
                  cleaned[key] = displayValue.trim() || val;
                } catch (e) {
                  cleaned[key] = val;
                }
              } else {
                cleaned[key] = val;
              }
            }
          }
          return cleaned;
        });

      const cleanedColumns = originalColumns
        .filter(col => col.field !== 'actions') // Remove actions column
        .map((col) => {
          const fieldName = col.field;
          const columnMeta = this.columnMeta.find(meta => 
            (meta.groupKey && meta.groupKey === fieldName) || meta.field === fieldName
          );
          
          return {
            ...col,
            title: columnMeta?.titles ? 
              (columnMeta.required ? `${columnMeta.titles} *` : columnMeta.titles) :
              (typeof col.title === "string"
                ? col.title
                    .replace(/<[^>]*>/g, "")
                    .trim()
                : col.title),
          };
        });
      this.table.setColumns(cleanedColumns);
      this.table.replaceData(cleanedData);
      this.table.download("xlsx", "data.xlsx");
      this.table.setColumns(originalColumns);
      this.table.replaceData(originalData);
    },
    checkIsRequiredError() {
      try {
        let errors = [];
        const tableColumns = this.table.getColumns().map(col => col.getField()).filter(field => field !== 'actions');
        const requiredFields = this.columnMeta.filter((m) => m.required && tableColumns.includes(m.groupKey || m.field));
        console.log("requiredFields", requiredFields.length, "tableColumns", tableColumns.length);

        if (this.table.getRows().length === 0) return true;

        this.table.getRows().forEach((rowObj) => {
          const rowData = rowObj.getData();
          console.log("Total cells in row:", rowObj.getCells().length);
          
          // Sort required fields by column order in table
          const sortedRequiredFields = requiredFields.sort((a, b) => {
            const fieldA = a.groupKey || a.field;
            const fieldB = b.groupKey || b.field;
            const indexA = tableColumns.indexOf(fieldA);
            const indexB = tableColumns.indexOf(fieldB);
            return indexA - indexB;
          });
          
          sortedRequiredFields.forEach((col) => {
            const fieldToCheck = col.groupKey || col.field;
            
            // Check if field should be editable based on editableIf condition
            let isFieldEditable = true;
            if (col.groupKey === 'panasonic_entity') {
              const region = rowData['panasonic_region'];
              const operatingCompany = rowData['operating_company'];
              isFieldEditable = region && operatingCompany;
            } else if (col.editableIf) {
              const parentValue = rowData[col.editableIf.field];
              isFieldEditable = parentValue === col.editableIf.value;
            }
            
            // Only validate if field is editable
            if (!isFieldEditable) return;
            
            const isEmpty =
              !rowData[fieldToCheck] ||
              (typeof rowData[fieldToCheck] === "string" &&
                !rowData[fieldToCheck].trim());

            if (isEmpty) {
              try {
                const cell = rowObj.getCell(fieldToCheck);
                if (cell) {
                  const element = cell?.getElement?.();
                  if (element) {
                    element.style.background = "#ffeaea";
                  }
                  errors.push({
                    row: rowObj.getPosition(),
                    field: fieldToCheck,
                    title: col.titles || col.field,
                  });
                }
              } catch (cellErr) {
                console.warn("Could not highlight cell:", cellErr);
              }
            } else {
              try {
                const cell = rowObj.getCell(fieldToCheck);
                const element = cell?.getElement?.();
                if (element) {
                  element.style.background = "";
                }
              } catch (cellErr) {
                console.warn("Could not clear cell highlight:", cellErr);
              }
            }
          });
        });

        if (errors.length) {
          this.$toast.error(
            this.$t("pages.batch_processing_table.required_fields") +
              "\nFirst error: Row " +
              errors[0].row +
              ", Field: " +
              errors[0].title
          );
          return true;
        }
        return false;
      } catch (err) {
        console.error("err in checking required err", err);
        return true;
      }
    },
    async handleSave(whileSubmit = false) {
      if (!(this.checkPermission("batchProcessing.edit") || this.checkPermission("batchProcessing.create"))) return false;

      const rawData = this.table
        .getData()
        .filter((row) => Object.values(row).some((val) => val && val !== ""));
      // Process payload with groupKey structure
      const payload = rawData
        .map((row) => {
          const cleanedRow = {};

          Object.keys(row).forEach((key) => {
            let value = row[key];
            
            // Fix object values like {"":"Yes"} but preserve arrays (multiselect)
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              const keys = Object.keys(value);
              if (keys.length === 1) {
                value = value[keys[0]];
              } else {
                value = '';
              }
            }
            
            // Check if this field is a multiselect column
            const isMultiselect = this.columnMeta.some(meta => 
              ((meta.groupKey && meta.groupKey === key) || meta.field === key) &&
              meta.editor === "list" && 
              meta.editorParams?.multiselect
            );
            
            // Convert comma-separated strings to arrays for multiselect fields
            if (isMultiselect && typeof value === 'string' && value.trim() !== '') {
              if (value.includes(',')) {
                // Handle comma-separated values from Excel or manual entry
                value = value.split(',').map(v => v.trim()).filter(v => v !== '');
              } else {
                // Single value - convert to array
                value = [value.trim()];
              }
            }
            
            // Convert select field labels to values for all list editors
            const fieldMeta = this.columnMeta.find(meta => 
              (meta.groupKey && meta.groupKey === key) || meta.field === key
            );
            if (fieldMeta?.editor === "list" && !fieldMeta?.editorParams?.multiselect && value && value !== '') {
              if (fieldMeta.editorParams?.values) {
                const option = fieldMeta.editorParams.values.find(opt => opt.label === value);
                value = option ? option.value : value;
              }
            }
            
            if (
              value &&
              value !== "" &&
              value !== null &&
              value !== undefined
            ) {
              cleanedRow[key] = value;
            } else if (Array.isArray(value) && value.length > 0) {
              // Preserve non-empty arrays (multiselect values)
              cleanedRow[key] = value;
            }
          });

          return cleanedRow;
        })
        .filter((row) => Object.keys(row).length > 1); // Keep rows with more than just 'id'
      return await this.SaveTheTabularData(payload, whileSubmit);
    },

    async SaveTheTabularData(payload, whileSubmit) {
      this.saveLoader = true;
      try {
        const { data } = await BatchAxios.post(
          `/batch-processing/save-batch-excel-data/${this.getBatchId}`,
          {
            headers: this.columnMeta,
            data: payload,
          }
        );
        if (data?.success) {
          if (!whileSubmit) {
            this.$toast.success(
              this.$t("pages.batch_processing_table.draft_saved_success")
            );
          }
          return true;
        }
        return false;
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
        return false;
      } finally {
        this.saveLoader = false;
      }
    },

    async submitTheData() {
      if (!(this.checkPermission("batchProcessing.edit") || this.checkPermission("batchProcessing.create"))) return;

      this.submitLoadig = true;
      try {
        if (this.checkIsRequiredError()) return;

        const saved = await this.handleSave(true);
        if (saved) {
          const { data } = await BatchAxios.post(
            `/batch-processing/submit_batch_with_data/${this.getBatchId}`
          );
          if (data?.success) {
            this.$toast.success(
              this.$t("pages.batch_processing_table.batch_created_success")
            );
            this.$router.push({ path: "/batch-processing" });
          }
        }
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
      } finally {
        this.submitLoadig = false;
      }
    },

    async refreshTheGrid() {
      await this.fetchBatchTabularData();
      this.initGrid();
    },
  },
};
</script>

<style>
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 10;
}

.modal-content {
  background: #fff;
  padding: 24px 32px 22px 32px;
  max-width: 510px;
  margin: 70px auto;
  border-radius: 10px;
  box-shadow: 0 8px 36px #0003;
}

.tabulator {
  height: 569px !important;
  border-radius: 8px;
}

/* .tabulator-header-contents {
    background-color:  var(--color-primary) !important;
} */
.tabulator .tabulator-header {
  /* background-color: var(--color-primary) !important; */
  /* color: white; */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.tabulator .tabulator-header .tabulator-col {
  background-color: var(--theme-color-main) !important;
  color: white;
}

.tabulator .tabulator-tableholder {
  background-color: white;
}

.tabulator-custom-required-asterisk {
  color: #e74c3c;
  margin-left: 2px;
  font-weight: bold;
  font-size: 1.1em;
}

.view-mode .tabulator-cell {
  cursor: not-allowed !important;
}

.tabulator .tabulator-cell.tabulator-editing {
  cursor: text !important;
}
</style>
